import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import {
  LmsRegistration,
  LmsRegistryService,
} from '../../../services/lms-registry.service';
import {
  GRAIN_PULSE_DURATION_MS,
  GrainPulseService,
} from '../../../services/grain-pulse.service';

/**
 * LTI 1.3 setup wizard.
 *
 * Multi-step dialog that walks an admin through registering an LMS as an
 * LTI 1.3 tool. Two-phase: we create a placeholder registration first
 * (so the admin gets stable URLs), then they paste the Moodle-side
 * Client ID + Deployment ID after Moodle's "configure tool" form
 * generated them.
 *
 * Visual pattern: follows the canonical setup-wizard from design.md
 * §"Setup Wizards (Stepper Dialogs)" — phase indicator, subway-station
 * stepper, flat .wizard-tile sections, grain-pulse + card-flash on
 * forward navigation. Linear=true because step 3 PATCHes the placeholder
 * created in step 1 (hard data dependency, see design.md §6).
 *
 * Opened from {@link LmsRegistrationsComponent} via the "LTI 1.3
 * einrichten" button.
 */
@Component({
  selector: 'app-lti-setup-wizard',
  templateUrl: './lti-setup-wizard.component.html',
  styleUrl: './lti-setup-wizard.component.scss',
})
export class LtiSetupWizardComponent implements OnDestroy {
  @ViewChild('stepper', { static: true }) stepper: any;

  @ViewChild('wizardCard', { read: ElementRef })
  private wizardCardRef?: ElementRef<HTMLElement>;
  private cardFlashTimeoutId: number | null = null;

  step1: FormGroup;
  step3: FormGroup;

  /** The placeholder registration created at the end of step 1.
   *  Holds `id` so step 3 can PATCH it; null until step 1 submitted. */
  registration: LmsRegistration | null = null;

  /** Wizard-level loading flag for the inline Save buttons. */
  saving = false;
  errorMessage: string | null = null;

  /** Tool-URLs, die der Admin in die Tool-Maske seines LMS einträgt.
   *
   *  Erst aus dem Backend-Host geraten, damit sofort etwas dasteht, dann von
   *  `GET /lms/lti/tool-urls` überschrieben. Das Raten allein lag in der
   *  K3s-Produktion falsch: al-lti läuft dort unter `lti-k3s.alphabees.de`,
   *  die Ratefunktion lieferte `lti.alphalearn.ai`. */
  toolUrls = {
    launch: `${this.ltiBaseUrl()}/lti/launch`,
    login: `${this.ltiBaseUrl()}/lti/login`,
    jwks: `${this.ltiBaseUrl()}/lti/jwks`,
    deepLinking: `${this.ltiBaseUrl()}/lti/deep-linking`,
  };

  // ── Phase indicator ────────────────────────────────────────────────
  /** Three logical phases the four wizard steps group into:
   *   1. Allgemein     — step 1 (Site identifizieren)
   *   2. Konfiguration — steps 2-3 (Tool-URLs anzeigen, IDs eintragen)
   *   3. Abschluss     — step 4 (Fertig)
   *  Bracket below each label spans exactly its steps via
   *  `phaseGridTemplate`. */
  readonly PHASE_LABEL_KEYS = [
    'tutor.ltiWizard.phases.general',
    'tutor.ltiWizard.phases.configuration',
    'tutor.ltiWizard.phases.done',
  ];

  /** Step counts per phase: [Allgemein, Konfiguration, Abschluss]. */
  private readonly phaseStepCounts: [number, number, number] = [1, 2, 1];

  /** First step index belonging to each phase. */
  private readonly phaseStartIndices: [number, number, number] = [0, 1, 3];

  /** Grid columns for `.wizard-phases` — weighted by step count so each
   *  bracket spans exactly its steps. */
  readonly phaseGridTemplate = this.phaseStepCounts
    .map((n) => `${Math.max(n, 1)}fr`)
    .join(' ');

  /** Tracks the active step (0-based). Drives `currentPhaseIndex`. */
  currentStepIndex = 0;

  /** Phase index (0/1/2) the current step belongs to. */
  get currentPhaseIndex(): number {
    const [_g, c, a] = this.phaseStartIndices;
    if (this.currentStepIndex >= a) return 2;
    if (this.currentStepIndex >= c) return 1;
    return 0;
  }

  /** Stepper handler — pulses the page-background grain field on forward
   *  navigation, then flashes the card border once the wave has run
   *  through. Keeps timing in lockstep with `GRAIN_PULSE_DURATION_MS`. */
  onStepChange(event: { index: number }): void {
    if (event.index > this.currentStepIndex) {
      this.grainPulse.pulse();
      this.scheduleCardFlash();
    }
    this.currentStepIndex = event.index;
  }

  private scheduleCardFlash(): void {
    if (this.cardFlashTimeoutId !== null) {
      window.clearTimeout(this.cardFlashTimeoutId);
    }
    this.cardFlashTimeoutId = window.setTimeout(() => {
      this.cardFlashTimeoutId = null;
      const el = this.wizardCardRef?.nativeElement;
      if (!el) return;
      // Re-trigger CSS animation via remove → forced reflow → add. Without
      // the reflow, consecutive forward-clicks within the animation window
      // would only flash once.
      el.classList.remove('is-flashing');
      void el.offsetWidth;
      el.classList.add('is-flashing');
    }, GRAIN_PULSE_DURATION_MS);
  }

  ngOnDestroy(): void {
    if (this.cardFlashTimeoutId !== null) {
      window.clearTimeout(this.cardFlashTimeoutId);
      this.cardFlashTimeoutId = null;
    }
  }

  constructor(
    private fb: FormBuilder,
    protected dialogRef: NbDialogRef<LtiSetupWizardComponent>,
    private lmsRegistry: LmsRegistryService,
    private router: Router,
    private grainPulse: GrainPulseService,
  ) {
    this.step1 = this.fb.group({
      display_name: ['', Validators.required],
      lti_issuer: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
    });
    this.step3 = this.fb.group({
      lti_client_id: ['', Validators.required],
      lti_deployment_id: [''],
      // Platform-side endpoints. al-lti redirects the OIDC login to
      // `auth` and verifies the launch JWT against `jwks`, so a wrong or
      // empty value here breaks every launch. Deliberately NOT prefilled:
      // this wizard is the path for every LMS without a plugin, and Moodle's
      // paths silently saved for an OpenOlat or Canvas site are wrong values
      // nobody notices. A Moodle admin fills them with one click instead
      // (`fillMoodleEndpoints`).
      lti_platform_auth_url: ['', Validators.required],
      lti_platform_token_url: [''],
      lti_platform_jwks_url: ['', Validators.required],
    });

    // Die gerateten URLs stehen sofort da; sobald das Backend antwortet,
    // gewinnt seine Konfiguration. Fällt der Aufruf aus, bleibt die Schätzung
    // stehen — besser als leere Felder.
    this.lmsRegistry.getLtiToolUrls().subscribe({
      next: (urls) => {
        this.toolUrls = {
          launch: urls.launch,
          login: urls.login,
          jwks: urls.jwks,
          deepLinking: urls.deep_linking,
        };
      },
      error: () => {},
    });
  }

  /** Moodle exposes its LTI endpoints at fixed paths under the site root.
   *  Other platforms don't — so this runs only when the admin asks for it
   *  (button in step 3), never on its own. Until 12.10.2026 it ran for every
   *  issuer and put Moodle paths into the form of any other LMS. */
  fillMoodleEndpoints(): void {
    const issuer: string = this.step1.value.lti_issuer;
    const base = (issuer || '').trim().replace(/\/+$/, '');
    if (!base) return;
    const paths: Record<string, string> = {
      lti_platform_auth_url: '/mod/lti/auth.php',
      lti_platform_token_url: '/mod/lti/token.php',
      lti_platform_jwks_url: '/mod/lti/certs.php',
    };
    for (const [control, path] of Object.entries(paths)) {
      const field = this.step3.get(control);
      if (!field) continue;
      field.setValue(`${base}${path}`);
      field.markAsDirty();
    }
  }

  /** Sofort-Schätzung des al-lti-Hosts aus dem Backend-Host, damit Schritt 2
   *  nicht leer aufblitzt. Verbindlich ist die Antwort von
   *  `GET /lms/lti/tool-urls`, die diese Werte im Konstruktor überschreibt. */
  private ltiBaseUrl(): string {
    const host = environment.tutorsBackend || '';
    if (host.includes('api.alphalearn.ai')) return 'https://lti.alphalearn.ai';
    if (host.includes('api.alphabees.de')) return 'https://lti.alphabees.de';
    if (host.includes('lassedesk.top')) return 'https://lti.lassedesk.top';
    return 'https://lti.alphalearn.ai';
  }

  // ──────────────────────────────────────────────────────────────────
  // Step transitions
  // ──────────────────────────────────────────────────────────────────

  submitStep1(): void {
    if (this.step1.invalid) {
      this.step1.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.errorMessage = null;

    // Create a placeholder registration so we have an `id` for the
    // PATCH at the end of step 3. lti_client_id is required by the
    // backend Pydantic model, so we seed a placeholder that step 3
    // will overwrite.
    this.lmsRegistry
      .createRegistration({
        source: 'lti13',
        display_name: this.step1.value.display_name,
        lti_issuer: this.step1.value.lti_issuer,
        lti_client_id: 'pending',
        // Tool-side URLs — ours, echoed back so the Portal can show them
        // again later. The LMS-side endpoints arrive with the step-3 PATCH.
        lti_login_url: this.toolUrls.login,
        lti_target_link_uri: this.toolUrls.launch,
        lti_jwks_url: this.toolUrls.jwks,
      })
      .subscribe({
        next: (reg) => {
          this.registration = reg;
          this.saving = false;
          this.stepper.next();
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = err?.error?.detail || 'Registrierung fehlgeschlagen.';
        },
      });
  }

  submitStep3(): void {
    if (this.step3.invalid || !this.registration) {
      this.step3.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.errorMessage = null;

    this.lmsRegistry
      .updateRegistration(this.registration.id, {
        lti_client_id: this.step3.value.lti_client_id,
        lti_deployment_id: this.step3.value.lti_deployment_id || undefined,
        lti_platform_auth_url: this.step3.value.lti_platform_auth_url,
        lti_platform_token_url: this.step3.value.lti_platform_token_url || undefined,
        lti_platform_jwks_url: this.step3.value.lti_platform_jwks_url,
      })
      .subscribe({
        next: (reg) => {
          this.registration = reg;
          this.saving = false;
          this.stepper.next();
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = err?.error?.detail || 'Aktualisieren fehlgeschlagen.';
        },
      });
  }

  copyToClipboard(value: string, ev?: MouseEvent): void {
    navigator.clipboard?.writeText(value);
    if (ev?.target instanceof HTMLElement) {
      const original = ev.target.textContent;
      ev.target.textContent = '✓';
      setTimeout(() => { (ev.target as HTMLElement).textContent = original; }, 800);
    }
  }

  goToPlacements(): void {
    this.dialogRef.close({ created: this.registration });
    this.router.navigate(['/tutors/placements'], { queryParams: { tab: 'lti' } });
  }

  /** Closing before step 3 leaves the placeholder created in step 1
   *  behind with `lti_client_id = 'pending'` — a row nothing can ever
   *  launch against, which then clutters the registrations list. Delete
   *  it on the way out; a completed registration is kept. */
  close(): void {
    const reg = this.registration;
    if (reg && reg.lti_client_id === 'pending') {
      this.lmsRegistry.deleteRegistration(reg.id).subscribe({
        next: () => this.dialogRef.close({ created: null }),
        error: () => this.dialogRef.close({ created: reg }),
      });
      return;
    }
    this.dialogRef.close({ created: reg });
  }
}
